import React, { useState, useRef, useEffect } from 'react';
import useSocket from '../services/useSocket';
import { useParams, Link } from 'react-router-dom';
import Message from './Message';
import { processChatRequest, getAssistantConfig, SOURCE, threadID, ROLE_ASSISTANT } from '../services/CondamindAPI';
import '../css/Chat.css';
import sendMessageImg from '../assets/send-button.svg';
import sendText from '../assets/send-button-text.svg';
import sendHover from '../assets/send-button-hover.svg';
import logoImg from '../assets/logo-conda.svg';
import MenuChat from '../components/MenuChat';
import Capsule from '../components/Capsule';
import backgroundConda from '../assets/background-conda.svg';
import { useNavigate } from 'react-router-dom';
import defaultConfig from '../resources/json/defaultAssistantConfig.json';

const errorMessage = defaultConfig.errorMessage;
const loadingMessageLarge = defaultConfig.loadingMessageLarge;

function Chat({ setLoading, loading, cvAssistantID, loadingScreen }) {
  const { asstID } = useParams();
  const assistantID = cvAssistantID || asstID || process.env.REACT_APP_DEFAULT_ASSISTANT;
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [showInitialLoading, setShowInitialLoading] = useState(true);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const [isChatResponding, setIsChatResponding] = useState(false);
  const [assistantConfig, setAssistantConfig] = useState({});
  const [suggestionCapsules, setSuggestionCapsules] = useState([]);
  let navigate = useNavigate();
  const socket = useSocket(process.env.REACT_APP_WEB_SOCKET_URL);
  let clientRegistered = false;

  useEffect(() => {
    if (threadID && !clientRegistered) {
      const externalReference = `${SOURCE}-${threadID}`
      socket.emit('register_client', { external_reference: externalReference });
      clientRegistered = true
    }

    if (socket && clientRegistered) {
      socket.on(`payment_update`, (data) => {
        setMessages(prevMessages => [...prevMessages, { role: ROLE_ASSISTANT, content: `${data.message}` }]);
      });
    }
  }, [socket, threadID]);

  useEffect(() => {
    if (chatWindowRef.current) {
      setTimeout(() => {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      }, 0);
    }
  }, [suggestionCapsules]);

  useEffect(() => {
    async function fetchAssistantConfig() {
      try {
        const config = await getAssistantConfig(assistantID, navigate);
        setAssistantConfig(config);
      } catch (error) {
        console.error('Error fetching assistant config:', error);
        // TODO: Handle error appropriately
      }
    }
    if (assistantID && assistantID !== '/') {
      fetchAssistantConfig();
    }
  }, [assistantID, navigate]);

  const calculateTypingSpeed = (messageLength) => {
    // Mientras más chico, más rápido
    const baseSpeed = 1000;
    return baseSpeed / messageLength;
  };

  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      requestAnimationFrame(() => {
        chatWindowRef.current.scrollTo({
          top: chatWindowRef.current.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
  };

  useEffect(() => {
    if (!loadingScreen && Object.keys(assistantConfig).length !== 0) {
      const welcomeMessage = assistantConfig.welcomeMessage;
      setMessages([
        {
          role: "assistant",
          content: welcomeMessage,
        },
      ]);

      const typingSpeed = calculateTypingSpeed(welcomeMessage.length);

      let index = 0;
      const timer = setInterval(() => {
        setMessages((prevMessages) => [
          {
            role: "assistant",
            content: welcomeMessage.slice(0, index + 1),
          },
        ]);
        index++;

        if (index === welcomeMessage.length) {
          clearInterval(timer);
        }
      }, typingSpeed);

      return () => clearInterval(timer);
    }
  }, [assistantConfig, loadingScreen]);

  const handleSendButton = async (userMessage, predefinedResponse) => {
    scrollToBottom();
    setInputMessage(userMessage);
    await sendQuestion(userMessage, predefinedResponse);
    scrollToBottom();
  };

  const sendQuestion = async (messageToSend, predefinedResponse) => {
    if (loading || isChatResponding) {
      return;
    }

    if (!messageToSend) {
      return;
    }

    setShowInitialLoading(true);
    setMessages(prevMessages => [...prevMessages, { role: "user", content: messageToSend }, { role: "loading", content: "..." }]);

    inputRef.current.blur();
    setLoading(true);
    setInputMessage('');

    let getRandomItem = (array) => {
      const randomIndex = Math.floor(Math.random() * array.length);
      return array[randomIndex];
    }

    const randomDelay = Math.random() * (5000 - 2000) + 2000;
    let delayTimer = setTimeout(() => {
      setShowInitialLoading(false);
      setMessages(prevMessages => [...prevMessages, { role: "loading_message", content: getRandomItem(assistantConfig.loadingMessages) || loadingMessageLarge }]);
    }, randomDelay + 100);

    try {
      setIsChatResponding(true);

      if (predefinedResponse) {
        await new Promise(resolve => setTimeout(resolve, randomDelay));
      }

      const data = predefinedResponse || await processChatRequest(assistantID, messageToSend);
      const currentSuggestionCapsules = data.response.suggestions || [];
      setSuggestionCapsules(currentSuggestionCapsules);

      clearTimeout(delayTimer);

      setLoading(false);
      setMessages(prevMessages => {
        const newMessages = prevMessages.filter(msg => msg.role !== "loading_message");
        const lastLoadingIndex = newMessages.map(msg => msg.role).lastIndexOf('loading');
        if (lastLoadingIndex !== -1) {
          newMessages[lastLoadingIndex] = { role: "assistant", content: data.response.content };
        }
        return newMessages;
      });

      setIsChatResponding(false);
      const container = document.querySelector('.container-suggestion-buttons');
      if (container) {
        container.style.display = currentSuggestionCapsules.length > 0 ? 'flex' : 'none';
      }

      if (chatWindowRef.current) {
        setTimeout(() => {
          chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
        }, 0);
      }

      if (inputRef.current) {
        inputRef.current.style.height = '20px';
      }
    } catch (error) {
      setIsChatResponding(false);
      setLoading(false);
      clearTimeout(delayTimer);
      setMessages(prevMessages => {
        const newMessages = prevMessages.filter(msg => msg.role !== 'loading_message');
        const lastLoadingIndex = newMessages.map(msg => msg.role).lastIndexOf('loading');
        if (lastLoadingIndex !== -1) {
          newMessages[lastLoadingIndex] = { role: "assistant", content: errorMessage };
        }
        return newMessages;
      });
      console.error('Hubo un error en el llamado API:', error);
    }
  };


  useEffect(() => {
    const inputElement = inputRef.current;

    if (inputElement) {
      const adjustInputHeight = () => {
        inputElement.style.height = '20px';
        const scrollHeight = inputElement.scrollHeight - 6;
        const currentHeight = inputElement.offsetHeight;

        if (scrollHeight > currentHeight) {
          inputElement.style.height = `${scrollHeight}px`;
        }
      };

      adjustInputHeight();

      inputElement.addEventListener('input', adjustInputHeight);

      return () => {
        inputElement.removeEventListener('input', adjustInputHeight);
      };
    }
  }, [inputMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      setInputMessage(prevMessage => prevMessage + '\n');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      sendQuestion(inputMessage);
    }
  };

  return (
    <div className="chat-container">
      <div className='logo-container'>
        <img className="logo p-3" alt='logo' src={logoImg} />
      </div>
      <div className='menu-chat-container'>
        <MenuChat assistantConfig={assistantConfig} handleSendButton={handleSendButton} />
        <div className={`chat-and-input-container ${assistantID ? 'with-assistant-id' : ''}`}>
          <img className="background-conda" src={backgroundConda} alt="background" />
          <div
            id="chat-window"
            className="chat-messages"
            ref={chatWindowRef}
          >
            {messages.map((message, index) => {
              if (message.role === "loading" && message.content === "..." && !showInitialLoading) {
                return null;
              }
              return (<Message
                key={index}
                message={message}
                copilot={assistantID}
                scrollToBottom={scrollToBottom}
              />)
            })}
          </div>
          <div className={`chat-input ${assistantID ? 'with-assistant-id' : ''}`}>
            <div className='suggestion-and-input-container'>
              {suggestionCapsules && (
                <div className="container-suggestion-buttons">
                  {suggestionCapsules.map((suggestion, index) => (
                    <Capsule
                      handleSendButton={handleSendButton}
                      suggestion={suggestion}
                      key={index}
                      shouldCloseMenu={false}
                    />
                  ))}
                </div>
              )}
              <div className='chat-input-container'>
                <div className="input-text-background"></div>
                <textarea
                  ref={inputRef}
                  className={`input-text ${loading ? 'loading' : ''}`}
                  value={inputMessage}
                  onChange={(e) => {
                    setInputMessage(e.target.value);
                    setIsHovered(false);
                  }}
                  placeholder="Escribí tu consulta acá..."
                  onKeyDown={handleKeyDown}
                />
                <button
                  className={`btn-send ${inputMessage.trim().length === 0 || loading || isChatResponding ? 'disabled' : ''}`}
                  onClick={() => {
                    if (inputMessage.trim().length !== 0) {
                      sendQuestion(inputMessage.trim());
                    }
                  }}
                  onMouseDown={() => setIsHovered(true)}
                  onMouseUp={() => setIsHovered(false)}
                  disabled={loading || isChatResponding}
                >
                  <img
                    src={isHovered ? sendHover : inputMessage ? sendText : sendMessageImg}
                    alt="Enviar"
                  />
                </button>
              </div>
            </div>
            <div className="terms-button">
              <Link className='terms-button' to="/terms">Ir a Términos y Condiciones</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat;